import { createContext, CSSProperties, useState } from "react";
import { buildClasses } from "../utils/buildClasses";
import { panelRiseDelay } from "./riseIn";
import { RYE_SLIDE_EASING } from "./motion";

/**
 * Layout gap between the two panels of a RyeMainContentSplitPane. The visible
 * gap is 2px less, as each panel's 1px border sits outside its content, so the
 * split shows the same 8px of page between panels as everywhere else.
 */
export const RYE_PANEL_SPLIT_GAP = 10;

export type RyePanelSplit = {
  /** Height of the split's lower section, including the gap, in pixels. */
  inset: number;
  /** How long the panels take to follow a change, or 0 to snap. */
  durationMs: number;
  /** Whether to draw the lower panel, including while it slides away. */
  showBottom: boolean;
};

const NO_SPLIT: RyePanelSplit = { inset: 0, durationMs: 0, showBottom: false };

/**
 * Lets a RyeMainContentSplitPane inside a screen split the screen's panel in
 * two: its lower section becomes a panel of its own, below a strip of page.
 */
export const RyePanelSplitContext = createContext<
  ((split: RyePanelSplit) => void) | null
>(null);

export function RyeScreenWrapper({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  const [split, setSplit] = useState<RyePanelSplit>(NO_SPLIT);
  // Only the app's first screen rises in, after the loading screen.
  const [riseDelay] = useState(panelRiseDelay);
  const rising = riseDelay !== null;

  return (
    <div
      className={buildClasses(
        "relative",
        "w-full",
        "h-full",
        "flex",
        "flex-col",
        "flex-grow",
        "flex-shrink",
        "min-w-1",
        // Fades and lifts the panel with its content; the surface below
        // grows its outline and shadow.
        { if: rising, then: "motion-safe:animate-rise" },
      )}
      style={
        {
          animationDelay: rising ? `${riseDelay}ms` : undefined,
          "--rise-delay": rising ? `${riseDelay}ms` : undefined,
          // Registered in index.css so it animates, and both panels follow
          // the split pane's slide frame by frame.
          "--rye-panel-split": `${split.inset}px`,
          transition:
            split.durationMs > 0
              ? `--rye-panel-split ${split.durationMs}ms ${RYE_SLIDE_EASING}`
              : "none",
        } as CSSProperties
      }
    >
      {/* The panel is drawn behind the content rather than by this element,
      so a split pane can divide it in two. */}
      <div
        aria-hidden
        className={buildClasses(
          "absolute",
          "inset-x-0",
          "top-0",
          RYE_PANEL_SURFACE,
          {
            if: rising,
            then: buildClasses(
              "motion-safe:animate-rise-outline",
              "motion-safe:before:animate-rise-outline",
              "[animation-delay:var(--rise-delay)]",
              "before:[animation-delay:var(--rise-delay)]",
            ),
          },
        )}
        style={{ bottom: "var(--rye-panel-split)" }}
      />
      {split.showBottom && (
        <div
          aria-hidden
          className={buildClasses(
            "absolute",
            "inset-x-0",
            "bottom-0",
            RYE_PANEL_SURFACE,
          )}
          style={{
            height: `max(0px, calc(var(--rye-panel-split) - ${RYE_PANEL_SPLIT_GAP - 2}px))`,
          }}
        />
      )}
      {/* Positioned, so it paints after the panels above. The 1px inset keeps
      content inside the panels' borders. */}
      <div
        className={buildClasses(
          "relative",
          "flex",
          "flex-col",
          "flex-1",
          "min-h-0",
          "min-w-0",
          "p-px",
        )}
      >
        <RyePanelSplitContext.Provider value={setSplit}>
          {children}
        </RyePanelSplitContext.Provider>
      </div>
    </div>
  );
}

/**
 * The surface of a main content panel, shared by screens and secondary
 * navigation sidebars, which sit beside them as panels of their own. Callers
 * position it.
 *
 * One continuous surface: the header, body and footer inside are transparent,
 * so they sit on it without visible seams. The outline is a warm brown, lighter
 * at the top and darker at the bottom, as if lit from above.
 *
 * The shadow it casts is drawn by a pseudo-element at a negative z-index, which
 * paints beneath every panel in the same stacking context. Side-by-side panels
 * then each cast onto the page, not onto each other. The panel itself must not
 * start a stacking context for this to work.
 */
export const RYE_PANEL_SURFACE = buildClasses(
  "rounded-[0.8rem]",
  "before:content-['']",
  "before:absolute",
  // Exactly over the border box, so the shadow starts at the outline and
  // follows its corners. Keep in step with the border width below.
  "before:-inset-px",
  "before:-z-10",
  "before:rounded-[inherit]",
  "before:shadow-main-content",
  "before:pointer-events-none",
  "bg-surface",
  "border",
  "border-edge/[0.12]",
  "border-t-edge/[0.08]",
  "border-b-edge/[0.2]",
);
